import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getMe,
  getStoredAccessToken,
  getStoredRefreshToken,
  logout as apiLogout,
  refreshSession,
  setStoredAccessToken,
  clearStoredSession,
} from '../api/accountPortalClient.js';
import { phIdentify, phReset } from '../utils/posthogEvents.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadUser = useCallback(async () => {
    const me = await getMe();
    setUser(me);
    phIdentify({
      email: me?.email,
      role: me?.companies?.[0]?.role,
      companyId: me?.companies?.[0]?.companyId,
    });
    return me;
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tryRefresh = async () => {
        const refreshed = await refreshSession();
        if (refreshed.authenticated && refreshed.accessToken) {
          await loadUser();
        } else {
          setUser(null);
        }
      };

      const token = getStoredAccessToken();
      const refreshToken = getStoredRefreshToken();
      if (token) {
        try {
          await loadUser();
        } catch {
          await tryRefresh();
        }
      } else if (refreshToken) {
        await tryRefresh();
      } else {
        setUser(null);
      }
    } catch (e) {
      clearStoredSession();
      setUser(null);
      setError(e?.message || 'Session expired');
    } finally {
      setLoading(false);
    }
  }, [loadUser]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const signIn = useCallback(
    async (accessToken) => {
      setStoredAccessToken(accessToken);
      await loadUser();
    },
    [loadUser]
  );

  const signOut = useCallback(async () => {
    await apiLogout();
    setUser(null);
    phReset();
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      isAuthenticated: !!user,
      signIn,
      signOut,
      refreshUser: loadUser,
    }),
    [user, loading, error, signIn, signOut, loadUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
